/**
 * Checks if the current Node environment is not a production one
 *
 * @returns {boolean}
 */
import { SourceItem } from '@/components/AccessPicker/types';

export function isDebugNodes() {
  return !!import.meta.env.VITE_DEBUG_QUESTIONNAIRE_NODES;
}

export function getLocationHash() {
  return window.location.hash.substr(1);
}

export function getRandomColor(alpha = 'FF') {
  const digits = '0123456789ABCDEF';
  let color = '#';

  for (let i = 0; i < 6; i += 1) {
    color += digits[Math.floor(Math.random() * 16)];
  }

  return `${color}${alpha}`;
}

export function formatXml(xml: string) {
  const PADDING = ' '.repeat(2); // set desired indent size here
  const reg = /(>)(<)(\/*)/g;
  let pad = 0;

  xml = xml.replace(reg, '$1\r\n$2$3');

  return xml.split('\r\n').map(node => {
    let indent = 0;

    if (node.match(/.+<\/\w[^>]*>$/)) {
      indent = 0;
    } else if (node.match(/^<\/\w/) && pad > 0) {
      pad -= 1;
    } else if (node.match(/^<\w[^>]*[^/]>.*$/)) {
      indent = 1;
    } else {
      indent = 0;
    }

    pad += indent;

    return `${PADDING.repeat(pad - indent)}${node}`;
  }).join('\r\n');
}

export function hexToRGBA(hex: string, opacity: number): string {
  const tempHex = hex.replace('#', '');
  const r = parseInt(tempHex.substring(0, 2), 16);
  const g = parseInt(tempHex.substring(2, 4), 16);
  const b = parseInt(tempHex.substring(4, 6), 16);

  return `rgba(${r},${g},${b},${opacity / 100})`;
}

export function resolveElement(key: string): HTMLElement | null {
  return document.querySelector(`[data-node-key=${key}]`);
}

export function getPreviousSiblings(elem: HTMLElement, filter?: (el: Element) => boolean, bail = true) {
  const siblings = [];
  let sibling = elem.previousElementSibling;

  while (sibling) {
    if (!filter) {
      siblings.push(sibling);
    } else if (filter(sibling)) {
      siblings.push(sibling);
    } else if (bail) {
      break;
    }

    sibling = sibling.previousElementSibling;
  }

  return siblings;
}

export function inferSplitValue(merged: any, options: any = { left: [], right: [] }) {
  // in case of object, reduce it to string
  if (typeof merged === 'object' && merged !== null && merged.text) {
    merged = merged.text;
  }

  let returnValue = {
    left: { text: '', value: '' },
    right: { text: '', value: '' },
  };

  if (!options.left || !options.right) {
    // bad options
    return returnValue;
  }

  if (!merged.trim()) {
    // merged text is empty. returning default empty
    return returnValue;
  }

  merged = merged.trim();

  const leftOptions = options.left.map((o: SourceItem): string => o.text.toString() || '');
  const rightOptions = options.right.map((o: SourceItem): string => o.text.toString() || '');

  leftOptions.forEach((leftText: string) => {
    if (leftText && merged.indexOf(leftText) === 0) {
      // ${leftText} found in merged
      const remains = merged.substring(leftText.length).trim();

      if (rightOptions.includes(remains) || remains.length === 0) {
        // found remains ${remains} in right options
        returnValue = {
          left: {
            value: options.left.find((item: any) => item?.text === leftText).value,
            text: leftText,
          },
          right: {
            value: options.right.find((item: any) => item?.text === remains).value,
            text: remains,
          },
        };

        return returnValue;
      }
    }

    return returnValue;
  });

  if (merged.indexOf(' ') === 0) {
    // We might only have the right part
    // we have a space at the beginning signaling that we might only have the "right side" part set
    rightOptions.forEach((rightText: string) => {
      if (rightText && merged === rightText) {
        // right value ${rightValue} confirmed
        returnValue = {
          left: {
            text: '',
            value: '',
          },
          right: {
            value: options.left.find((item: any) => item?.text === rightText).value,
            text: rightText,
          },
        };

        return returnValue;
      }

      return returnValue;
    });
  }

  return returnValue;
}

// Jest/JSDOM wrapper
export function safeQuerySelector(person: Element, query: string): Element | null {
  try {
    return person.querySelector(query);
  } catch (e) {
    return null;
  }
}

// Jest/JSDOM wrapper
export function safeQuerySelectors(person: Element, query: string): Element[] | never[] {
  try {
    return Array.from(person.querySelectorAll(query));
  } catch (e) {
    return [];
  }
}
